import { apiFetch } from '../../utils/apiClient';
import { STUDIO_COPY } from '../config';
import { recordedVoiceFilename } from '../music/recorderMime';
import {
  AcquireError,
  DurationRerenderError,
  InvalidEmailError,
  PortalError,
  TwitchSignInRequiredError,
  type AcquireErrorCode,
  type AcquireSuccess,
  type CheckoutSuccess,
  type DurationRerenderErrorCode,
  type Entitlement,
  type HighlightJob,
  type PortalSuccess,
  type Profile,
  type ProfileUpdate,
  type ReelSummary,
  type StudioCheckoutPlan,
  type Vod,
} from './types';

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function throwIfTwitchRequired(response: Response, body: unknown): void {
  if (response.status !== 401) return;
  const message =
    typeof body === 'string'
      ? body
      : body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : 'sign in with Twitch';
  if (/sign in with twitch/i.test(message) || message === 'sign in with Twitch') {
    throw new TwitchSignInRequiredError(
      typeof body === 'string' ? body : 'sign in with Twitch'
    );
  }
}

function asRecord(body: unknown): Record<string, unknown> {
  return body && typeof body === 'object' && !Array.isArray(body)
    ? (body as Record<string, unknown>)
    : {};
}

function parseAcquireErrorCode(body: unknown): AcquireErrorCode | null {
  const record = asRecord(body);
  const code = typeof record.error === 'string' ? record.error : null;
  if (code === 'beta_spend_cap_reached') return 'daily_capacity_reached';
  if (
    code === 'upgrade_required' ||
    code === 'monthly_cap_reached' ||
    code === 'reel_already_exists' ||
    code === 'generation_ceiling_reached' ||
    code === 'reel_name_required' ||
    code === 'daily_capacity_reached' ||
    code === 'not_allowlisted' ||
    code === 'generation_paused' ||
    code === 'vod_unsupported_game'
  ) {
    return code;
  }
  return null;
}

function throwIfDurationRerenderFailed(response: Response, body: unknown): void {
  if (response.ok) return;
  const record = asRecord(body);
  const code = typeof record.error === 'string' ? record.error : '';
  const message =
    typeof record.message === 'string' && record.message.trim()
      ? record.message
      : `duration rerender failed: ${response.status}`;
  let mapped: DurationRerenderErrorCode = 'failed';
  if (code === 'source_unavailable') mapped = 'source_unavailable';
  else if (code === 'not_ready_to_rerender') mapped = 'not_ready_to_rerender';
  else if (code === 'feature_disabled') mapped = 'feature_disabled';
  else if (code === 'not_found') mapped = 'not_found';
  throw new DurationRerenderError(mapped, {
    status: response.status,
    message,
  });
}

/** Live: GET /api/rtts/twitch/vods */
export async function liveGetVods(): Promise<Vod[]> {
  const response = await apiFetch('/api/rtts/twitch/vods');
  const body = await readBody(response);
  throwIfTwitchRequired(response, body);
  if (!response.ok) {
    throw new Error(`getVods failed: ${response.status}`);
  }
  if (!Array.isArray(body)) {
    throw new Error('getVods: expected Vod[]');
  }
  return body as Vod[];
}

/** Live: GET /api/rtts/me/entitlement */
export async function liveGetEntitlement(): Promise<Entitlement> {
  const response = await apiFetch('/api/rtts/me/entitlement');
  const body = await readBody(response);
  throwIfTwitchRequired(response, body);
  if (!response.ok) {
    throw new Error(`getEntitlement failed: ${response.status}`);
  }
  return body as Entitlement;
}

/**
 * Live: POST /api/rtts/highlights/acquire
 * Request body uses `twitch_vod_id` (VOD object's field is `vod_id`).
 */
export async function liveAcquire(
  twitchVodId: string,
  options?: {
    recut?: boolean;
    reelName?: string;
    force?: boolean;
    durationTier?: string;
  }
): Promise<AcquireSuccess> {
  const payload: {
    twitch_vod_id: string;
    recut?: boolean;
    force?: boolean;
    reel_name?: string;
    duration_tier?: string;
  } = {
    twitch_vod_id: twitchVodId,
  };
  if (options?.recut) {
    payload.recut = true;
  }
  if (options?.force) {
    payload.force = true;
  }
  if (options?.durationTier) {
    payload.duration_tier = options.durationTier;
  }
  if (options && 'reelName' in options) {
    payload.reel_name = options.reelName ?? '';
  }
  const response = await apiFetch('/api/rtts/highlights/acquire', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await readBody(response);
  throwIfTwitchRequired(response, body);

  if (response.status === 402) {
    const code = parseAcquireErrorCode(body) ?? 'upgrade_required';
    const record = asRecord(body);
    if (code === 'generation_ceiling_reached') {
      throw new AcquireError('generation_ceiling_reached', {
        status: 402,
        message: code,
        period_resets_at:
          typeof record.period_resets_at === 'string'
            ? record.period_resets_at
            : undefined,
        period_cap:
          typeof record.period_cap === 'number' ? record.period_cap : undefined,
      });
    }
    throw new AcquireError(code === 'monthly_cap_reached' ? 'monthly_cap_reached' : 'upgrade_required', {
      status: 402,
      message: code,
      period_resets_at:
        typeof record.period_resets_at === 'string' ? record.period_resets_at : undefined,
      period_cap:
        typeof record.period_cap === 'number' ? record.period_cap : undefined,
    });
  }

  if (response.status === 429) {
    const record = asRecord(body);
    const code = parseAcquireErrorCode(body);
    if (code === 'daily_capacity_reached') {
      throw new AcquireError('daily_capacity_reached', {
        status: 429,
        message:
          typeof record.message === 'string'
            ? record.message
            : STUDIO_COPY.dailyCapacity,
        period_resets_at:
          typeof record.period_resets_at === 'string'
            ? record.period_resets_at
            : undefined,
        period_cap:
          typeof record.period_cap === 'number' ? record.period_cap : undefined,
      });
    }
  }

  if (response.status === 403) {
    const record = asRecord(body);
    if (parseAcquireErrorCode(body) === 'not_allowlisted') {
      throw new AcquireError('not_allowlisted', {
        status: 403,
        message:
          typeof record.message === 'string'
            ? record.message
            : STUDIO_COPY.notAllowlisted,
      });
    }
  }

  if (response.status === 503) {
    const record = asRecord(body);
    if (parseAcquireErrorCode(body) === 'generation_paused') {
      throw new AcquireError('generation_paused', {
        status: 503,
        message:
          typeof record.message === 'string'
            ? record.message
            : STUDIO_COPY.generationPaused,
      });
    }
  }

  // TODO(seam): confirm standalone re-run code/shape (expect 409 / reel_already_exists)
  // TODO(seam): confirm 409 body includes existing job_id
  if (response.status === 409) {
    const record = asRecord(body);
    const jobId =
      typeof record.job_id === 'string'
        ? record.job_id
        : record.job && typeof record.job === 'object' && 'id' in (record.job as object)
          ? String((record.job as { id: unknown }).id)
          : undefined;
    throw new AcquireError('reel_already_exists', {
      status: 409,
      message: 'reel_already_exists',
      job_id: jobId,
    });
  }

  if (response.status === 400 && parseAcquireErrorCode(body) === 'reel_name_required') {
    const record = asRecord(body);
    throw new AcquireError('reel_name_required', {
      status: 400,
      message:
        typeof record.message === 'string'
          ? record.message
          : 'Name your reel to cut it.',
    });
  }

  if (response.status === 400 && parseAcquireErrorCode(body) === 'vod_unsupported_game') {
    const record = asRecord(body);
    throw new AcquireError('vod_unsupported_game', {
      status: 400,
      message:
        typeof record.message === 'string'
          ? record.message
          : STUDIO_COPY.vodUnsupportedGame,
    });
  }

  if (!response.ok) {
    throw new Error(`acquire failed: ${response.status}`);
  }

  // TODO(seam): confirm standalone acquire is unwrapped {job} vs LSC {success,job}
  const record = asRecord(body);
  const jobRaw = record.job;
  if (!jobRaw || typeof jobRaw !== 'object') {
    throw new Error('acquire: expected { job }');
  }
  const job = jobRaw as HighlightJob;
  return { job };
}

/** Live: GET /api/rtts/highlights/jobs/{id} */
export async function liveGetJob(id: string): Promise<{ job: HighlightJob }> {
  const response = await apiFetch(
    `/api/rtts/highlights/jobs/${encodeURIComponent(id)}`
  );
  const body = await readBody(response);
  throwIfTwitchRequired(response, body);
  if (!response.ok) {
    throw new Error(`getJob failed: ${response.status}`);
  }
  const record = asRecord(body);
  if (record.job && typeof record.job === 'object') {
    return { job: record.job as HighlightJob };
  }
  return { job: record as unknown as HighlightJob };
}

/** Live: GET /api/rtts/highlights/jobs/{id}/candidates */
export async function liveGetCandidates(
  jobId: string
): Promise<import('./types').CandidatesSuccess> {
  const response = await apiFetch(
    `/api/rtts/highlights/jobs/${encodeURIComponent(jobId)}/candidates`
  );
  const body = await readBody(response);
  throwIfTwitchRequired(response, body);
  if (!response.ok) {
    throw new Error(`getCandidates failed: ${response.status}`);
  }
  const record = asRecord(body);
  const candidates = Array.isArray(record.candidates) ? record.candidates : [];
  const curation =
    record.curation && typeof record.curation === 'object'
      ? (record.curation as import('./types').CurationState)
      : { status: '' };
  return {
    candidates: candidates as import('./types').CurationCandidate[],
    curation,
    source_preview_url:
      typeof record.source_preview_url === 'string'
        ? record.source_preview_url
        : null,
    source_preview_expires_at:
      typeof record.source_preview_expires_at === 'string'
        ? record.source_preview_expires_at
        : null,
    trim_enabled: Boolean(record.trim_enabled),
    curate_enabled: Boolean(record.curate_enabled),
  };
}

/** Live: POST /api/rtts/highlights/jobs/{id}/curate */
export async function liveSubmitCurate(
  jobId: string,
  selected: string[],
  reelName?: string
): Promise<{ job: HighlightJob }> {
  const payload: { selected: string[]; reel_name?: string } = { selected };
  if (reelName !== undefined) {
    payload.reel_name = reelName;
  }
  const response = await apiFetch(
    `/api/rtts/highlights/jobs/${encodeURIComponent(jobId)}/curate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  const body = await readBody(response);
  throwIfTwitchRequired(response, body);
  if (!response.ok) {
    throw new Error(`curate failed: ${response.status}`);
  }
  const record = asRecord(body);
  const jobRaw = record.job;
  if (!jobRaw || typeof jobRaw !== 'object') {
    throw new Error('curate: expected { job }');
  }
  return { job: jobRaw as HighlightJob };
}

/** Live: POST /api/rtts/highlights/jobs/{id}/refine */
export async function liveSubmitRefine(
  jobId: string,
  clips: import('./types').RefineClip[],
  music?: import('./types').RefineMusicOptions | null
): Promise<{ job: HighlightJob; docked_reel: boolean }> {
  const payload: Record<string, unknown> = { clips };
  if (music !== undefined && music !== null) {
    payload.music_id = music.music_id;
    payload.music_mode = music.music_mode;
    payload.game_gain_db = music.game_gain_db;
    payload.music_gain_db = music.music_gain_db;
    payload.music_loop = music.music_loop;
    payload.music_fade_in_s = music.music_fade_in_s;
    payload.music_fade_out_s = music.music_fade_out_s;
  } else if (music === null) {
    payload.music_id = null;
  }
  const response = await apiFetch(
    `/api/rtts/highlights/jobs/${encodeURIComponent(jobId)}/refine`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  const body = await readBody(response);
  throwIfTwitchRequired(response, body);
  if (!response.ok) {
    throw new Error(`refine failed: ${response.status}`);
  }
  const record = asRecord(body);
  const jobRaw = record.job;
  if (!jobRaw || typeof jobRaw !== 'object') {
    throw new Error('refine: expected { job }');
  }
  return {
    job: jobRaw as HighlightJob,
    docked_reel: Boolean(record.docked_reel),
  };
}

/** Live: POST /api/rtts/highlights/jobs/{id}/rerender-duration */
export async function liveSubmitDurationRerender(
  jobId: string,
  durationTier: string
): Promise<{ job: HighlightJob; docked_reel: boolean }> {
  const response = await apiFetch(
    `/api/rtts/highlights/jobs/${encodeURIComponent(jobId)}/rerender-duration`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ duration_tier: durationTier }),
    }
  );
  const body = await readBody(response);
  throwIfTwitchRequired(response, body);
  if (response.status === 401) {
    const record = asRecord(body);
    throw new TwitchSignInRequiredError(
      typeof record.message === 'string' ? record.message : 'sign in with Twitch'
    );
  }
  throwIfDurationRerenderFailed(response, body);
  const record = asRecord(body);
  const jobRaw = record.job;
  if (!jobRaw || typeof jobRaw !== 'object') {
    throw new Error('duration rerender: expected { job }');
  }
  return {
    job: jobRaw as HighlightJob,
    docked_reel: Boolean(record.docked_reel),
  };
}

/** Live: POST /api/rtts/highlights/jobs/{id}/accept — dock + clean re-render. */
export async function liveSubmitAccept(
  jobId: string,
  music?: import('./types').RefineMusicOptions | null
): Promise<{
  job: HighlightJob;
  docked_reel: boolean;
  already_accepted: boolean;
}> {
  const payload: Record<string, unknown> = {};
  if (music !== undefined && music !== null) {
    payload.music_id = music.music_id;
    payload.music_mode = music.music_mode;
    payload.game_gain_db = music.game_gain_db;
    payload.music_gain_db = music.music_gain_db;
    payload.music_loop = music.music_loop;
    payload.music_fade_in_s = music.music_fade_in_s;
    payload.music_fade_out_s = music.music_fade_out_s;
  } else if (music === null) {
    payload.music_id = null;
  }
  const response = await apiFetch(
    `/api/rtts/highlights/jobs/${encodeURIComponent(jobId)}/accept`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  const body = await readBody(response);
  throwIfTwitchRequired(response, body);

  if (response.status === 402) {
    const code = parseAcquireErrorCode(body) ?? 'upgrade_required';
    const record = asRecord(body);
    throw new AcquireError(
      code === 'monthly_cap_reached' ? 'monthly_cap_reached' : 'upgrade_required',
      {
        status: 402,
        message: code,
        period_resets_at:
          typeof record.period_resets_at === 'string'
            ? record.period_resets_at
            : undefined,
        period_cap:
          typeof record.period_cap === 'number' ? record.period_cap : undefined,
      }
    );
  }

  if (!response.ok) {
    throw new Error(`accept failed: ${response.status}`);
  }
  const record = asRecord(body);
  const jobRaw = record.job;
  if (!jobRaw || typeof jobRaw !== 'object') {
    throw new Error('accept: expected { job }');
  }
  return {
    job: jobRaw as HighlightJob,
    docked_reel: Boolean(record.docked_reel),
    already_accepted: Boolean(record.already_accepted),
  };
}

/** Live: GET /api/rtts/music/manifest */
export async function liveGetMusicManifest(): Promise<{
  tracks: import('../music/types').MusicTrack[];
}> {
  const response = await apiFetch('/api/rtts/music/manifest');
  const body = await readBody(response);
  throwIfTwitchRequired(response, body);
  if (!response.ok) {
    throw new Error(`music manifest failed: ${response.status}`);
  }
  const { loadTracks } = await import('../music/loadTracks');
  const record = asRecord(body);
  const tracks = loadTracks(
    Array.isArray(body) ? body : record.tracks ?? body
  );
  return { tracks };
}

/** Live: POST /api/rtts/me/reels/{jobId}/addons/music */
export async function liveApplyMusicAddon(
  jobId: string,
  musicId: string,
  mix: { music_volume: number; reel_audio_volume: number } = {
    music_volume: 45,
    reel_audio_volume: 100,
  }
): Promise<{ job: HighlightJob }> {
  const response = await apiFetch(
    `/api/rtts/me/reels/${encodeURIComponent(jobId)}/addons/music`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        music_id: musicId,
        music_volume: mix.music_volume,
        reel_audio_volume: mix.reel_audio_volume,
      }),
    }
  );
  const body = await readBody(response);
  throwIfTwitchRequired(response, body);
  if (response.status === 402) {
    throw new AcquireError('upgrade_required', {
      status: 402,
      message: 'Music add-ons are included with Pro.',
    });
  }
  if (response.status === 429) {
    const record = asRecord(body);
    throw Object.assign(
      new Error(
        String(record.message || STUDIO_COPY.addonsCooldown)
      ),
      { error: 'music_addon_cooldown', status: 429 }
    );
  }
  if (!response.ok) {
    throw new Error(`applyMusicAddon failed: ${response.status}`);
  }
  const record = asRecord(body);
  const jobRaw = record.job;
  if (!jobRaw || typeof jobRaw !== 'object') {
    throw new Error('applyMusicAddon: expected { job }');
  }
  return { job: jobRaw as HighlightJob };
}

/** Live: DELETE /api/rtts/me/reels/{jobId}/addons/music */
export async function liveRemoveMusicAddon(
  jobId: string
): Promise<{ job: HighlightJob }> {
  const response = await apiFetch(
    `/api/rtts/me/reels/${encodeURIComponent(jobId)}/addons/music`,
    { method: 'DELETE' }
  );
  const body = await readBody(response);
  throwIfTwitchRequired(response, body);
  if (!response.ok) {
    throw new Error(`removeMusicAddon failed: ${response.status}`);
  }
  const record = asRecord(body);
  const jobRaw = record.job;
  if (!jobRaw || typeof jobRaw !== 'object') {
    throw new Error('removeMusicAddon: expected { job }');
  }
  return { job: jobRaw as HighlightJob };
}

async function liveAddonJobResponse(
  response: Response,
  body: unknown,
  failLabel: string
): Promise<{ job: HighlightJob }> {
  throwIfTwitchRequired(response, body);
  if (response.status === 402) {
    throw new AcquireError('upgrade_required', {
      status: 402,
      message: 'Music add-ons are included with Pro.',
    });
  }
  if (response.status === 429) {
    const record = asRecord(body);
    throw Object.assign(
      new Error(String(record.message || STUDIO_COPY.addonsCooldown)),
      { error: 'music_addon_cooldown', status: 429 }
    );
  }
  if (!response.ok) {
    throw new Error(`${failLabel} failed: ${response.status}`);
  }
  const record = asRecord(body);
  const jobRaw = record.job;
  if (!jobRaw || typeof jobRaw !== 'object') {
    throw new Error(`${failLabel}: expected { job }`);
  }
  return { job: jobRaw as HighlightJob };
}

/** Live: POST /api/rtts/me/reels/{jobId}/addons/mix */
export async function liveApplyAddonMix(
  jobId: string,
  mix: {
    music_id?: string | null;
    music_volume: number;
    reel_audio_volume: number;
    voice_volume?: number;
    clear_music?: boolean;
  }
): Promise<{ job: HighlightJob }> {
  const payload: Record<string, unknown> = {
    music_volume: mix.music_volume,
    reel_audio_volume: mix.reel_audio_volume,
  };
  if (mix.music_id) payload.music_id = mix.music_id;
  if (typeof mix.voice_volume === 'number') payload.voice_volume = mix.voice_volume;
  if (mix.clear_music) payload.clear_music = true;
  const response = await apiFetch(
    `/api/rtts/me/reels/${encodeURIComponent(jobId)}/addons/mix`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  const body = await readBody(response);
  return liveAddonJobResponse(response, body, 'applyAddonMix');
}

/** Live: POST /api/rtts/me/reels/{jobId}/addons/watermark */
export async function liveApplyWatermarkOptIn(
  jobId: string,
  enabled: boolean
): Promise<{ job: HighlightJob }> {
  const response = await apiFetch(
    `/api/rtts/me/reels/${encodeURIComponent(jobId)}/addons/watermark`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    }
  );
  const body = await readBody(response);
  return liveAddonJobResponse(response, body, 'applyWatermarkOptIn');
}

/** Live: POST /api/rtts/me/reels/{jobId}/addons/voice */
export async function liveStoreVoiceAddon(
  jobId: string,
  file: Blob,
  opts: { voice_source: 'recorded' | 'uploaded'; rights_ack?: boolean }
): Promise<{ job: HighlightJob }> {
  const form = new FormData();
  const filename =
    opts.voice_source === 'uploaded'
      ? 'voice-upload'
      : recordedVoiceFilename(file);
  form.append('file', file, filename);
  form.append('voice_source', opts.voice_source);
  if (opts.rights_ack) form.append('rights_ack', 'true');
  const response = await apiFetch(
    `/api/rtts/me/reels/${encodeURIComponent(jobId)}/addons/voice`,
    { method: 'POST', body: form }
  );
  const body = await readBody(response);
  return liveAddonJobResponse(response, body, 'storeVoiceAddon');
}

/** Live: POST /api/rtts/me/reels/{jobId}/addons/music/upload */
export async function liveStoreMusicAddon(
  jobId: string,
  file: Blob,
  opts: { rights_ack?: boolean } = {}
): Promise<{ job: HighlightJob }> {
  const form = new FormData();
  const filename = file instanceof File && file.name ? file.name : 'music-upload';
  form.append('file', file, filename);
  if (opts.rights_ack) form.append('rights_ack', 'true');
  const response = await apiFetch(
    `/api/rtts/me/reels/${encodeURIComponent(jobId)}/addons/music/upload`,
    { method: 'POST', body: form }
  );
  const body = await readBody(response);
  return liveAddonJobResponse(response, body, 'storeMusicAddon');
}

/** Live: DELETE /api/rtts/me/reels/{jobId}/addons/voice */
export async function liveRemoveVoiceAddon(
  jobId: string
): Promise<{ job: HighlightJob }> {
  const response = await apiFetch(
    `/api/rtts/me/reels/${encodeURIComponent(jobId)}/addons/voice`,
    { method: 'DELETE' }
  );
  const body = await readBody(response);
  throwIfTwitchRequired(response, body);
  if (!response.ok) {
    throw new Error(`removeVoiceAddon failed: ${response.status}`);
  }
  const record = asRecord(body);
  const jobRaw = record.job;
  if (!jobRaw || typeof jobRaw !== 'object') {
    throw new Error('removeVoiceAddon: expected { job }');
  }
  return { job: jobRaw as HighlightJob };
}

/**
 * Live: POST /api/rtts/billing/checkout
 * Body: { plan: "monthly" | "annual" } → { checkout_url }
 *
 * TODO(seam): confirm RTTS_CHECKOUT_SUCCESS_URL/CANCEL_URL match
 * `/studio/upgrade/success` and `/studio/upgrade/cancel` before enabling billing.
 * TODO(seam): confirm { plan } param + RTTS_PAID_PRICE_ID_ANNUAL with backend.
 */
export async function liveCheckout(
  plan: StudioCheckoutPlan
): Promise<CheckoutSuccess> {
  const payload =
    plan === 'pack5' ? { product: 'pack5' } : { plan };
  const response = await apiFetch('/api/rtts/billing/checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await readBody(response);
  throwIfTwitchRequired(response, body);
  if (!response.ok) {
    throw new Error(`checkout failed: ${response.status}`);
  }
  const record = asRecord(body);
  if (typeof record.checkout_url !== 'string' || !record.checkout_url) {
    throw new Error('checkout: expected { checkout_url }');
  }
  return { checkout_url: record.checkout_url };
}

/** Live: POST /api/rtts/me/highlight-interest */
export async function liveRecordHighlightInterest(): Promise<{
  recorded: boolean;
  already: boolean;
}> {
  const response = await apiFetch('/api/rtts/me/highlight-interest', {
    method: 'POST',
  });
  const body = await readBody(response);
  throwIfTwitchRequired(response, body);
  if (!response.ok) {
    throw new Error(`highlight-interest failed: ${response.status}`);
  }
  const record = asRecord(body);
  return {
    recorded: Boolean(record.recorded),
    already: Boolean(record.already),
  };
}

/** Live: GET /api/rtts/me/feedback */
export async function liveGetFeedbackChannel(): Promise<{
  mailto: string;
  channel: string;
}> {
  const response = await apiFetch('/api/rtts/me/feedback');
  const body = await readBody(response);
  throwIfTwitchRequired(response, body);
  if (!response.ok) {
    throw new Error(`getFeedbackChannel failed: ${response.status}`);
  }
  const record = asRecord(body);
  return {
    mailto: typeof record.mailto === 'string' ? record.mailto : '',
    channel: typeof record.channel === 'string' ? record.channel : 'in_product',
  };
}

/** Live: POST /api/rtts/me/feedback */
export async function liveSubmitFeedback(
  message: string,
  category = 'general'
): Promise<{ recorded: boolean; feedback_id?: string }> {
  const response = await apiFetch('/api/rtts/me/feedback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, category }),
  });
  const body = await readBody(response);
  throwIfTwitchRequired(response, body);
  if (!response.ok) {
    throw new Error(`submitFeedback failed: ${response.status}`);
  }
  const record = asRecord(body);
  return {
    recorded: Boolean(record.recorded),
    feedback_id:
      typeof record.feedback_id === 'string' ? record.feedback_id : undefined,
  };
}

/** Live: GET /api/rtts/me/profile */
export async function liveGetProfile(): Promise<Profile> {
  const response = await apiFetch('/api/rtts/me/profile');
  const body = await readBody(response);
  if (!response.ok) {
    throw new Error(`getProfile failed: ${response.status}`);
  }
  return body as Profile;
}

/** Live: PUT /api/rtts/me/profile */
export async function liveUpdateProfile(patch: ProfileUpdate): Promise<Profile> {
  const response = await apiFetch('/api/rtts/me/profile', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  const body = await readBody(response);
  if (response.status === 400) {
    const record = asRecord(body);
    if (record.error === 'invalid_email') {
      throw new InvalidEmailError(
        typeof record.message === 'string' ? record.message : 'Enter a valid email'
      );
    }
  }
  if (!response.ok) {
    throw new Error(`updateProfile failed: ${response.status}`);
  }
  return body as Profile;
}

/** Live: POST /api/rtts/billing/portal → { portal_url } */
export async function liveCreatePortalSession(): Promise<PortalSuccess> {
  const response = await apiFetch('/api/rtts/billing/portal', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  const body = await readBody(response);
  if (response.status === 409) {
    throw new PortalError('no_active_subscription', {
      status: 409,
      message: 'no_active_subscription',
    });
  }
  if (response.status === 404) {
    throw new PortalError('billing_unavailable', {
      status: 404,
      message: 'billing_unavailable',
    });
  }
  if (!response.ok) {
    throw new PortalError('portal_failed', {
      status: response.status,
      message: 'portal_failed',
    });
  }
  const record = asRecord(body);
  if (typeof record.portal_url !== 'string' || !record.portal_url) {
    throw new PortalError('portal_failed', {
      status: 500,
      message: 'portal_failed',
    });
  }
  return { portal_url: record.portal_url };
}

/** Live: GET /api/rtts/me/reels → { reels: ReelSummary[] } */
export async function liveGetReels(): Promise<{ reels: ReelSummary[] }> {
  const response = await apiFetch('/api/rtts/me/reels', { cache: 'no-store' });
  const body = await readBody(response);
  if (!response.ok) {
    throw new Error(`getReels failed: ${response.status}`);
  }
  const record = asRecord(body);
  const reels = record.reels;
  if (!Array.isArray(reels)) {
    throw new Error('getReels: expected { reels }');
  }
  return { reels: reels as ReelSummary[] };
}

/** Live: POST /api/rtts/me/reels/{jobId}/share */
export async function liveEnsureShareLink(
  jobId: string
): Promise<{ share_url: string }> {
  const response = await apiFetch(
    `/api/rtts/me/reels/${encodeURIComponent(jobId)}/share`,
    { method: 'POST' }
  );
  const body = await readBody(response);
  if (!response.ok) {
    throw new Error(`ensureShareLink failed: ${response.status}`);
  }
  const record = asRecord(body);
  const share_url = typeof record.share_url === 'string' ? record.share_url : '';
  if (!share_url.startsWith('https://scoutlytics.gg/r/')) {
    throw new Error('ensureShareLink: expected scoutlytics.gg/r/ URL');
  }
  return { share_url };
}

/** Live: DELETE /api/rtts/me/reels/{jobId} */
export async function liveDeleteReel(jobId: string): Promise<void> {
  const response = await apiFetch(
    `/api/rtts/me/reels/${encodeURIComponent(jobId)}`,
    { method: 'DELETE' }
  );
  if (!response.ok) {
    throw new Error(`deleteReel failed: ${response.status}`);
  }
}

/** Live: PATCH /api/rtts/me/reels/{jobId}/name */
export async function liveRenameReel(
  jobId: string,
  reelName: string
): Promise<ReelSummary> {
  const response = await apiFetch(
    `/api/rtts/me/reels/${encodeURIComponent(jobId)}/name`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reel_name: reelName }),
    }
  );
  const body = await readBody(response);
  if (!response.ok) {
    throw new Error(`renameReel failed: ${response.status}`);
  }
  const record = asRecord(body);
  const reel = record.reel;
  if (!reel || typeof reel !== 'object') {
    throw new Error('renameReel: expected { reel }');
  }
  return reel as ReelSummary;
}
